'use client'

// Personalization data helpers (login-system R2 wave 2): browser -> PostgREST/RPC with the
// publishable key, RLS-scoped to the signed-in user. All calls are fire-safe: failures
// degrade to "no personalization", never to a broken page.
import { SUPABASE_CONFIGURED, supabaseBrowser } from './client'
import type { Database } from './database.types'

export type ContentKind = Database['public']['Enums']['content_kind']
export type ContentIndexRow = Database['public']['Tables']['content_index']['Row']
export type PreferenceRow = Database['public']['Tables']['user_preferences']['Row']

/** Map a site path to the content_kind taxonomy (null = not personalization-tracked). */
export function contentKindForPath(path: string): ContentKind | null {
  const p = path.replace(/\/+$/, '') || '/'
  if (p.startsWith('/workflows/')) return 'workflow'
  if (p === '/workflows') return 'page'
  if (p.startsWith('/knowledge-hub/')) return 'knowledge_hub'
  if (/^\/portfolio\/[^/]+\/[^/]+$/.test(p)) return 'product'
  if (/^\/portfolio\/[^/]+$/.test(p)) return 'vendor'
  if (p === '/portfolio') return 'page'
  return null
}

/** Count a view (per-user per-path counters via the record_content_view RPC). */
export async function recordView(path: string): Promise<void> {
  if (!SUPABASE_CONFIGURED) return
  const kind = contentKindForPath(path)
  if (!kind) return
  try {
    await supabaseBrowser().rpc('record_content_view', { p_path: path, p_type: kind })
  } catch {
    /* personalization is best-effort */
  }
}

export async function listSavedWorkflows(): Promise<PreferenceRow[]> {
  if (!SUPABASE_CONFIGURED) return []
  const { data } = await supabaseBrowser()
    .from('user_preferences')
    .select('*')
    .eq('kind', 'saved_workflow')
    .order('sort_order', { ascending: true })
    .order('created_at', { ascending: false })
  return data || []
}

export async function isWorkflowSaved(path: string): Promise<boolean> {
  if (!SUPABASE_CONFIGURED) return false
  const { data } = await supabaseBrowser()
    .from('user_preferences')
    .select('id')
    .eq('kind', 'saved_workflow')
    .eq('content_path', path)
    .maybeSingle()
  return Boolean(data)
}

export async function toggleWorkflowSaved(path: string, title: string): Promise<boolean> {
  if (!SUPABASE_CONFIGURED) return false
  const sb = supabaseBrowser()
  const { data } = await sb
    .from('user_preferences')
    .select('id')
    .eq('kind', 'saved_workflow')
    .eq('content_path', path)
    .maybeSingle()
  if (data) {
    await sb.from('user_preferences').delete().eq('id', data.id)
    return false
  }
  const { data: u } = await sb.auth.getUser()
  if (!u.user) return false
  const { error } = await sb.from('user_preferences').insert({
    user_id: u.user.id,
    kind: 'saved_workflow',
    content_type: 'workflow',
    content_path: path,
    title,
  })
  return !error
}

/** Favourites (kind 'bookmark') — the Supabase revival of the Clerk-era favourites
 * feature, which had been invisible since the 2026-07-14 cutover. Any content page
 * can be favourited; the dashboard lists them. */
export async function listBookmarks(): Promise<PreferenceRow[]> {
  if (!SUPABASE_CONFIGURED) return []
  const { data } = await supabaseBrowser()
    .from('user_preferences')
    .select('*')
    .eq('kind', 'bookmark')
    .order('created_at', { ascending: false })
  return data || []
}

/** Toggle a favourite; returns the new state (true = now favourited). */
export async function toggleBookmark(path: string, title: string): Promise<boolean> {
  if (!SUPABASE_CONFIGURED) return false
  const sb = supabaseBrowser()
  const { data } = await sb
    .from('user_preferences')
    .select('id')
    .eq('kind', 'bookmark')
    .eq('content_path', path)
    .maybeSingle()
  if (data) {
    await sb.from('user_preferences').delete().eq('id', data.id)
    return false
  }
  const { data: u } = await sb.auth.getUser()
  if (!u.user) return false
  const { error } = await sb.from('user_preferences').insert({
    user_id: u.user.id,
    kind: 'bookmark',
    content_type: contentKindForPath(path) || 'page',
    content_path: path,
    title,
  })
  return !error
}

/** Most recently viewed content (joined against content_index for display titles). */
export async function recentlyViewed(limit = 4): Promise<ContentIndexRow[]> {
  if (!SUPABASE_CONFIGURED) return []
  const sb = supabaseBrowser()
  const { data: eng } = await sb
    .from('user_content_engagement')
    .select('content_path,last_viewed_at')
    .order('last_viewed_at', { ascending: false })
    .limit(limit)
  const paths = (eng || []).map((e) => e.content_path)
  if (!paths.length) return []
  const { data: rows } = await sb.from('content_index').select('*').in('content_path', paths)
  const byPath = new Map((rows || []).map((r) => [r.content_path, r]))
  return paths.map((p) => byPath.get(p)).filter(Boolean) as ContentIndexRow[]
}

export async function suggestions(limit = 6): Promise<ContentIndexRow[]> {
  if (!SUPABASE_CONFIGURED) return []
  const { data } = await supabaseBrowser().rpc('get_content_suggestions', { p_limit: limit })
  return (data as ContentIndexRow[]) || []
}
